import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Check, Calendar, Target, Flame, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const WEEKDAYS = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag'];
const WEEKDAYS_SHORT = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

export default function WeekPlannerPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekData, setWeekData] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [newTask, setNewTask] = useState('');
  const [loading, setLoading] = useState(true);
  const [habits, setHabits] = useState([]);
  const [todos, setTodos] = useState([]);
  const [upcomingDates, setUpcomingDates] = useState([]);

  // Get week dates
  const getWeekDates = (offset = 0) => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1 + (offset * 7));
    
    return Array.from({ length: 7 }).map((_, i) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      return date;
    });
  };

  const weekDates = getWeekDates(weekOffset);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (hasAccess) {
      loadAllData();
    }
  }, [hasAccess, weekOffset]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load week plan, habits, todos, and dates in parallel
      const [weekRes, habitsRes, todosRes, datesRes] = await Promise.all([
        axios.get(`${API}/api/planner/week`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/habits`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/todos`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/dates`, { headers: getAuthHeader() })
      ]);
      
      // Process week data
      const planData = weekRes.data;
      if (planData && planData.days) {
        // Convert days object to date-based keys
        const daysMap = {};
        const weekStart = getWeekDates(weekOffset)[0];
        Object.entries(planData.days).forEach(([dayName, dayData], index) => {
          const dayDate = new Date(weekStart);
          dayDate.setDate(weekStart.getDate() + index);
          daysMap[dayDate.toISOString().split('T')[0]] = dayData;
        });
        setWeekData(daysMap);
      }
      
      setHabits(habitsRes.data || []);
      setTodos(todosRes.data || []);
      
      // Calculate upcoming dates
      const dates = datesRes.data || [];
      const upcoming = dates.filter(d => {
        const days = daysUntil(d.date);
        return days >= 0 && days <= 30;
      }).sort((a, b) => daysUntil(a.date) - daysUntil(b.date));
      setUpcomingDates(upcoming);
      
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const daysUntil = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateStr);
    targetDate.setFullYear(today.getFullYear());
    if (targetDate < today) targetDate.setFullYear(today.getFullYear() + 1);
    return Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
  };

  const getDateKey = (date) => date.toISOString().split('T')[0];

  const getDayData = (date) => {
    const key = getDateKey(date);
    return weekData[key] || { tasks: [], notes: '' };
  };

  const saveWeekPlan = async (newWeekData) => {
    try {
      // Convert date-based keys back to day names
      const weekStart = weekDates[0];
      const days = {};
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      
      weekDates.forEach((date, index) => {
        const key = getDateKey(date);
        days[dayNames[index]] = newWeekData[key] || { tasks: [], notes: '' };
      });
      
      await axios.put(`${API}/api/planner/week`, { days }, {
        headers: getAuthHeader()
      });
    } catch (err) {
      console.error('Failed to save week plan:', err);
      toast.error('Kunde inte spara');
    }
  };

  const addTask = async (date) => {
    if (!newTask.trim()) return;
    const key = getDateKey(date);
    const dayData = getDayData(date);
    const newTasks = [...dayData.tasks, { id: Date.now().toString(), text: newTask, done: false }];
    const newWeekData = { ...weekData, [key]: { ...dayData, tasks: newTasks } };
    setWeekData(newWeekData);
    setNewTask('');
    await saveWeekPlan(newWeekData);
  };

  const toggleTask = async (date, taskId) => {
    const key = getDateKey(date);
    const dayData = getDayData(date);
    const newTasks = dayData.tasks.map(t => t.id === taskId ? { ...t, done: !t.done } : t);
    const newWeekData = { ...weekData, [key]: { ...dayData, tasks: newTasks } };
    setWeekData(newWeekData);
    await saveWeekPlan(newWeekData);
  };

  const deleteTask = async (date, taskId) => {
    const key = getDateKey(date);
    const dayData = getDayData(date);
    const newTasks = dayData.tasks.filter(t => t.id !== taskId);
    const newWeekData = { ...weekData, [key]: { ...dayData, tasks: newTasks } };
    setWeekData(newWeekData);
    await saveWeekPlan(newWeekData);
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="weekplanner-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Veckoöversikt</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att använda veckoöversikten.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="weekplanner-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Veckoöversikt</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8 flex justify-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="weekplanner-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/planning')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Veckoöversikt</h1>
            <p className="text-xs text-muted-foreground">
              {weekDates[0].toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} - {weekDates[6].toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
            </p>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w - 1)} className="rounded-full w-8 h-8 p-0" data-testid="week-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)} className="rounded-full text-xs" data-testid="week-today">
              Idag
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w + 1)} className="rounded-full w-8 h-8 p-0" data-testid="week-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Week grid */}
        <div className="grid grid-cols-7 gap-2 mb-6">
          {weekDates.map((date, i) => {
            const dateKey = getDateKey(date);
            const isToday = dateKey === today;
            const dayData = getDayData(date);
            const completedTasks = dayData.tasks.filter(t => t.done).length;
            const totalTasks = dayData.tasks.length;

            return (
              <button
                key={i}
                onClick={() => setSelectedDay(selectedDay === i ? null : i)}
                data-testid={`weekday-${i}`}
                className={`p-3 rounded-xl border text-center transition-all ${
                  isToday 
                    ? 'bg-sky-50 border-sky-200' 
                    : selectedDay === i 
                      ? 'bg-white border-primary shadow-md'
                      : 'bg-white border-border/30 hover:border-primary/30'
                }`}
              >
                <p className={`text-xs font-medium ${isToday ? 'text-sky-600' : 'text-muted-foreground'}`}>
                  {WEEKDAYS_SHORT[i]}
                </p>
                <p className={`text-lg font-bold ${isToday ? 'text-sky-600' : ''}`}>
                  {date.getDate()}
                </p>
                {totalTasks > 0 && (
                  <div className="mt-1 flex justify-center gap-0.5">
                    {completedTasks === totalTasks ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <span className="text-xs text-muted-foreground">{completedTasks}/{totalTasks}</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected day detail */}
        {selectedDay !== null && (
          <div className="bg-white rounded-2xl border border-border/30 p-4 mb-6 animate-fade-in">
            <h3 className="font-semibold mb-4">
              {WEEKDAYS[selectedDay]} {weekDates[selectedDay].getDate()}/{weekDates[selectedDay].getMonth() + 1}
            </h3>
            
            {/* Add task */}
            <div className="bg-secondary/30 rounded-xl p-3 mb-4 space-y-2">
              <p className="text-sm font-medium text-foreground">Lägg till en uppgift för denna dag:</p>
              <div className="flex gap-2">
                <Input
                  placeholder="T.ex. Möte kl 10, träning, ringa mamma ..."
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTask(weekDates[selectedDay])}
                  className="rounded-xl flex-1"
                  data-testid="week-task-input"
                />
                <Button onClick={() => addTask(weekDates[selectedDay])} className="rounded-xl px-4" data-testid="week-add-task">
                  <Plus className="w-4 h-4 mr-2" />
                  Lägg till
                </Button>
              </div>
            </div>

            {/* Tasks */}
            <div className="space-y-2">
              {getDayData(weekDates[selectedDay]).tasks.map(task => (
                <div 
                  key={task.id}
                  className={`flex items-center gap-2 p-2 rounded-lg ${task.done ? 'bg-emerald-50' : 'bg-slate-50'}`}
                  data-testid={`week-task-${task.id}`}
                >
                  <button
                    onClick={() => toggleTask(weekDates[selectedDay], task.id)}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      task.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300'
                    }`}
                    data-testid={`toggle-task-${task.id}`}
                  >
                    {task.done && <Check className="w-3 h-3" />}
                  </button>
                  <span className={`flex-1 text-sm ${task.done ? 'line-through text-muted-foreground' : ''}`}>
                    {task.text}
                  </span>
                  <button
                    onClick={() => deleteTask(weekDates[selectedDay], task.id)}
                    className="text-muted-foreground hover:text-destructive text-sm"
                    data-testid={`delete-task-${task.id}`}
                  >
                    ×
                  </button>
                </div>
              ))}
              {getDayData(weekDates[selectedDay]).tasks.length === 0 && (
                <p className="text-muted-foreground text-sm text-center py-4">Inga uppgifter för denna dag</p>
              )}
            </div>
          </div>
        )}

        {/* Quick overview */}
        <div className="grid md:grid-cols-3 gap-4">
          {/* Active todos */}
          <div className="bg-white rounded-2xl border border-border/30 p-4">
            <h4 className="font-semibold flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-sky-500" />
              Att göra
            </h4>
            {todos.filter(t => !t.completed).slice(0, 5).map(todo => (
              <div key={todo.id} className="text-sm text-muted-foreground py-1 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                {todo.text}
              </div>
            ))}
            {todos.filter(t => !t.completed).length === 0 && (
              <p className="text-sm text-muted-foreground">Inga aktiva uppgifter</p>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/todos')} className="mt-2 text-xs">
              Visa alla →
            </Button>
          </div>

          {/* Habits */}
          <div className="bg-white rounded-2xl border border-border/30 p-4">
            <h4 className="font-semibold flex items-center gap-2 mb-3">
              <Flame className="w-5 h-5 text-orange-500" />
              Dagens vanor
            </h4>
            {habits.slice(0, 5).map(habit => {
              const isCompletedToday = habit.completions?.includes(today);
              return (
                <div key={habit.id} className="text-sm py-1 flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                    isCompletedToday ? 'bg-emerald-500 text-white' : 'bg-slate-100'
                  }`}>
                    {isCompletedToday && <Check className="w-3 h-3" />}
                  </span>
                  <span className={isCompletedToday ? 'text-emerald-600' : 'text-muted-foreground'}>
                    {habit.name}
                  </span>
                </div>
              );
            })}
            {habits.length === 0 && (
              <p className="text-sm text-muted-foreground">Inga vanor skapade</p>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/habits')} className="mt-2 text-xs">
              Visa alla →
            </Button>
          </div>

          {/* Upcoming dates */}
          <div className="bg-white rounded-2xl border border-border/30 p-4">
            <h4 className="font-semibold flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-pink-500" />
              Kommande datum
            </h4>
            {upcomingDates.length > 0 ? (
              upcomingDates.slice(0, 3).map(d => (
                <div key={d.id} className="text-sm py-1 flex items-center justify-between">
                  <span className="text-muted-foreground">{d.name}</span>
                  <span className="text-xs text-pink-600">{daysUntil(d.date)} dagar</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Inga datum sparade</p>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/important-dates')} className="mt-2 text-xs">
              Visa alla →
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
